/**
 * Marine Reference API — boat make → model → year cascade.
 *
 * Backs the boat-entry step of the estimate flow, the way the automotive
 * reference endpoints back the car one. Reads the server's `boat_make` /
 * `boat_model` tables, populated from the vehDB feed.
 *
 * Two things about this API that are easy to get wrong:
 *
 * 1. Every response is enveloped as `{ success, data }` — callers here get the
 *    unwrapped array.
 * 2. Model names contain `/` and `(*)` — e.g. `H190B/BS(*)`. They MUST be
 *    URL-encoded or the un-encoded slash breaks the route. Never interpolate a
 *    model name straight into a path or query string.
 */

import { HTTPService } from './httpService';

const API = new HTTPService();

/** Unwrap the `{ success, data }` envelope; tolerate a bare array. */
const unwrap = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
};

/**
 * Surface the server's own message rather than a generic axios string —
 * these routes return useful 400s ("makeId or make is required") that the
 * caller should be able to show.
 */
const messageFrom = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

/**
 * All boat makes, optionally filtered by substring.
 * @param {string} [q] substring filter on make_name
 * @returns {Promise<Array>} [{ id, make_name, ... }]
 */
export const getBoatMakes = async (q, { silent = false } = {}) => {
  try {
    const params = {};
    if (q) params.q = q;
    const { data } = await API.get('/api/boats/makes', params, { silent });
    return unwrap(data);
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not load boat makes.'));
  }
};

/**
 * Distinct model NAMES for a make — not per-year rows. The year range on each
 * row is a hint for the next step.
 *
 * Exactly one of makeId / make is required; the server 400s otherwise.
 *
 * @param {{ makeId?: number|string, make?: string, q?: string }} opts
 * @returns {Promise<Array>} [{ make_id, make_name, model_name, year_count, earliest_year, latest_year }]
 */
export const getBoatModels = async ({ makeId, make, q } = {}, { silent = false } = {}) => {
  if (makeId === undefined && !make) {
    throw new Error('makeId or make is required');
  }
  try {
    const params = {};
    if (makeId !== undefined && makeId !== null) params.makeId = makeId;
    else params.make = make;
    if (q) params.q = q;
    const { data } = await API.get('/api/boats/models', params, { silent });
    return unwrap(data);
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not load boat models.'));
  }
};

/**
 * Year rows for one model, carrying the FULL dimensions and the vendor UUID —
 * so there is no need to call getBoatModel() afterwards in the normal flow.
 *
 * Both makeId and model are required. `model` is encoded here; callers pass the
 * raw name.
 *
 * @returns {Promise<Array>} [{ model_year, loa_ft, beam_ft, length_raw, beam_raw,
 *   boat_class, model_type, hull_code, hull_height_in, hull_height_source,
 *   boat_model_source, boat_model_uuid }]
 */
export const getBoatYears = async (makeId, model, { silent = false } = {}) => {
  if (makeId === undefined || makeId === null || !model) {
    throw new Error('makeId and model are required');
  }
  try {
    // HTTPService serialises params, but model names carry `/` and `(*)`;
    // encoding here guarantees it regardless of the serialiser's behaviour.
    const { data } = await API.get(
      `/api/boats/years?makeId=${encodeURIComponent(makeId)}&model=${encodeURIComponent(model)}`,
      {},
      { silent }
    );
    return unwrap(data);
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not load boat years.'));
  }
};

/**
 * Re-resolve a single boat model by its VENDOR uuid. NOT needed in the normal
 * cascade — getBoatYears already returns everything. Use for deep-linking or
 * re-resolving a saved boat. 404s on an unknown uuid.
 *
 * Additionally returns `hull_height_known` and a ready-made `hull_height_prompt`.
 */
export const getBoatModel = async (uuid, source = 'vehdb', { silent = false } = {}) => {
  if (!uuid) throw new Error('uuid is required');
  try {
    const { data } = await API.get(
      `/api/boats/model/${encodeURIComponent(uuid)}`,
      { source },
      { silent }
    );
    return data?.data ?? data ?? null;
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not load that boat model.'));
  }
};

// `buildMarineAttributes` and `hinWarning` deliberately live in
// `src/util/marineService.js` instead of here — they are pure, and that module
// has zero imports so the Node ESM harness can test them directly. This module
// imports HTTPService and cannot be harness-tested.

const marineReferenceService = {
  getBoatMakes,
  getBoatModels,
  getBoatYears,
  getBoatModel,
};

export default marineReferenceService;
